import {
  ActivityStepDraft,
  createActivityStep,
} from "./activityStepTypes";

const DURATION_NUMBER =
  "(?:\\d+|zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty(?:[-\\s](?:one|two|three|four|five|six|seven|eight|nine))?|thirty(?:[-\\s](?:one|two|three|four|five|six|seven|eight|nine))?|forty(?:[-\\s](?:one|two|three|four|five|six|seven|eight|nine))?|fifty(?:[-\\s](?:one|two|three|four|five|six|seven|eight|nine))?|sixty)";
const DURATION_COMPONENT = `${DURATION_NUMBER}\\s*[-–—]?\\s*(?:hours?|hrs?|hr|h|minutes?|mins?|min|m|seconds?|secs?|sec|s)`;
const DURATION_GROUP = new RegExp(
  `\\b(${DURATION_COMPONENT}(?:\\s*(?:,|and)\\s*${DURATION_COMPONENT}){0,2})\\b`,
  "gi",
);

function cleanInstruction(value: string): string {
  return value
    .trim()
    .replace(/\s*[:;,]\s*$/, "")
    .replace(/\s+/g, " ");
}

export function splitActivitySteps(rawValue: string): string[] {
  const lines = String(rawValue || "").trim().split(/\r?\n/);
  const steps: string[] = [];
  let current = "";

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;
    const numbered = line.match(/^(?:\((\d+)\)|(\d+)[.)])\s*(.*)$/);
    const bullet = line.match(/^(?:\*\s+|-\s+|-(?=[A-Za-z])|•\s*)(.+)$/);
    if (numbered || bullet) {
      if (current) steps.push(current.trim());
      current = numbered ? numbered[3].trim() : bullet![1].trim();
      return;
    }
    current = current ? `${current} ${line}` : line;
  });

  if (current) steps.push(current.trim());
  return steps;
}

export interface TimerDetection {
  hasDuration: boolean;
  hasConflict: boolean;
  totalSeconds: number;
  label: string;
}

const numberWords: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
};

function parsedNumber(value: string): number | null {
  const normalized = value.toLowerCase().replace(/-/g, " ").trim();
  if (/^\d+$/.test(normalized)) return Number(normalized);
  if (numberWords[normalized] !== undefined) return numberWords[normalized];
  const [tens, ones, ...extra] = normalized.split(/\s+/);
  if (
    !extra.length &&
    numberWords[tens] >= 20 &&
    numberWords[tens] % 10 === 0 &&
    numberWords[ones] >= 1 &&
    numberWords[ones] <= 9
  ) {
    return numberWords[tens] + numberWords[ones];
  }
  return null;
}

function durationGroupSeconds(group: string): number | null {
  const component = new RegExp(
    `(${DURATION_NUMBER})\\s*[-–—]?\\s*(hours?|hrs?|hr|h|minutes?|mins?|min|m|seconds?|secs?|sec|s)`,
    "gi",
  );
  let total = 0;
  let count = 0;
  for (const match of Array.from(group.matchAll(component))) {
    const amount = parsedNumber(match[1]);
    if (amount === null) return null;
    const unit = match[2].toLowerCase();
    total += /^(?:hours?|hrs?|hr|h)$/.test(unit)
      ? amount * 3600
      : /^(?:minutes?|mins?|min|m)$/.test(unit)
        ? amount * 60
        : amount;
    count += 1;
  }
  return count && total > 0 ? total : null;
}

export function detectTimerDuration(text: string): TimerDetection {
  const matches = Array.from(String(text || "").matchAll(DURATION_GROUP));
  if (!matches.length) {
    return { hasDuration: false, hasConflict: false, totalSeconds: 0, label: "" };
  }
  if (matches.length > 1) {
    return { hasDuration: false, hasConflict: true, totalSeconds: 0, label: "" };
  }
  const totalSeconds = durationGroupSeconds(matches[0][1]);
  if (!totalSeconds) {
    return { hasDuration: false, hasConflict: false, totalSeconds: 0, label: "" };
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const label = [
    minutes ? `${minutes} min` : "",
    seconds ? `${seconds} sec` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return { hasDuration: true, hasConflict: false, totalSeconds, label };
}

function looksLikeQa(text: string): boolean {
  const source = text.replace(/^Q(?:uestion)?\s*:\s*/i, "").trim();
  return (
    source.endsWith("?") ||
    /^(?:what|when|where|why|who|which|how|describe|rate)\b/i.test(source)
  );
}

function parseStep(text: string): ActivityStepDraft {
  const imageMatch = text.match(/\s+Image:\s*(.+?)\s*$/i);
  if (imageMatch) {
    return createActivityStep({
      type: "image",
      text: cleanInstruction(text.slice(0, imageMatch.index)),
      imageName: imageMatch[1].trim(),
    });
  }

  const urlMatch = text.match(/https?:\/\/[^\s]+/i);
  if (urlMatch) {
    return createActivityStep({
      type: "video",
      text: cleanInstruction(text.replace(urlMatch[0], "")),
      url: urlMatch[0].trim(),
    });
  }

  const duration = detectTimerDuration(text);
  if (
    duration.hasDuration &&
    /\b(?:wait|continue|practice|sit|stand|stay|remain|hold|breathe|repeat|focus|look|listen|watch|brush|wash|touch|keep|spend|perform|do)\b/i.test(
      text,
    )
  ) {
    return createActivityStep({ type: "timer", text: text.trim() });
  }

  if (
    /^(?:please\s+)?upload\b/i.test(text) ||
    /\bupload\s+(?:a|an|the|your)?\s*(?:photo|picture|image|file|screenshot|document)\b/i.test(
      text,
    )
  ) {
    return createActivityStep({ type: "upload", text: text.trim() });
  }

  if (looksLikeQa(text)) {
    return createActivityStep({
      type: "qa",
      text: text.replace(/^Q(?:uestion)?\s*:\s*/i, "").trim(),
    });
  }

  return createActivityStep({ type: "text", text: text.trim() });
}

export function parseActivitySteps(rawValue: string): ActivityStepDraft[] {
  const steps = splitActivitySteps(rawValue).map(parseStep);
  return steps.length ? steps : [createActivityStep()];
}
